import cx from "classnames";
import localFont from "next/font/local";
import type { AppProps } from "next/app";
import { FaustProvider } from "@faustwp/core";
import React, { useEffect, useMemo } from "react";
import { ClientMountProvider } from "@/providers/ClientMountProvider";
import { SoundProvider } from "@/providers/SoundContext";
import Page from "@/layouts/page";
import { PageLoadingProvider, PagePresence } from "@/providers/LoadingContext";
import { TrackingProvider } from "@/providers/TrackingContext";
import { ViewSizeProvider } from "@/providers/ViewSizeContext";
import { CarouselOffsetProvider } from "@/providers/CarouselOffsetContext";
import { ScrollRestorationManager } from "@/hooks/ScrollRestorationManager";
import { useDynamicMaxWidth } from "@/hooks/useDynamicMaxWidth"; // TODO: Temporary, remove later -Gabe
import TrackingGtm from "@/components/Tracking/TrackingGtm";
import isRockHallTvTemplate from '@/utils/isRhtvTemplate';
import getRouteKey from "@/utils/getRouteKey";

import "@styles/global.scss";
import "@root/faust.config.ts";

// -------------------- Fonts

const GenerationSansPro = localFont({
  src: "../assets/fonts/GenerationSansPro-Medium.woff2",
  variable: "--font-generation-sans-pro",
});

const Sohne = localFont({
  src: [
    {
      path: "../assets/fonts/Söhne-Buch.woff2",
      weight: "400",
    },
    {
      path: "../assets/fonts/Söhne-Kräftig.woff2",
      weight: "500",
    },
    {
      path: "../assets/fonts/Söhne-Halbfett.woff2",
      weight: "600",
    },
  ],
  variable: "--font-sohne",
});

// -------------------- App

export default function MyApp({ Component, pageProps, router }: AppProps<FaustPageProps<any>>) {
  const fonts = cx(GenerationSansPro.variable, Sohne.variable);
  const routeKey = useMemo(() => getRouteKey(router.asPath), [router.asPath]);

  // ----------

  // Disable NextJS prefetching
  useMemo(() => {
    router.prefetch = async () => {};
  }, [router]);

  // Detect if device is touch-capable
  useEffect(() => {
    const onTouchStart = () => {
      document.body.classList.add("touch");
      window.removeEventListener("touchstart", onTouchStart);
    };
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
    };
  }, []);

  // ----------

  // Detect "Rock Hall TV" page template
  const seedNode = pageProps?.__SEED_NODE__;
  const isTemplateRockHallTv = isRockHallTvTemplate(seedNode, router.query);

  // Should we use the scroll restoration manager?
  const useScrollRestoration = !isTemplateRockHallTv;

  // Temporary: Apply dynamic max-width from URL query params
  useDynamicMaxWidth(); // TODO: Temporary, remove later -Gabe

  // -------------------- Render

  return (
    <FaustProvider pageProps={pageProps}>
      <ClientMountProvider>
        <PageLoadingProvider>
          {useScrollRestoration && <ScrollRestorationManager />}
          <ViewSizeProvider>
            <TrackingProvider>
              <SoundProvider>
                <CarouselOffsetProvider>
                  <Page {...pageProps} className={fonts}>
                    <div id="portal" />
                    <PagePresence>
                      <Component {...pageProps} key={routeKey} />
                    </PagePresence>
                  </Page>
                </CarouselOffsetProvider>
              </SoundProvider>
            </TrackingProvider>
          </ViewSizeProvider>
        </PageLoadingProvider>
      </ClientMountProvider>
      <TrackingGtm />
    </FaustProvider>
  );
}
